import React, { useCallback, useEffect, useRef, useState } from 'react';
import { Badge, Button, Flex, Text } from '@radix-ui/themes';
import { Bell, Volume2, VolumeX } from 'lucide-react';
import { terminalOrderStatus } from './orderStatus';

const soundKey=scope=>`bplatform:order-alert-sound:${scope}`;
const savedSound=scope=>{try{return localStorage.getItem(soundKey(scope))==='on';}catch{return false;}};

export default function OrderAlerts({orders,scope,enabled,onOpenOrders}) {
  const seen=useRef(null);
  const audio=useRef(null);
  const pendingAudio=useRef(null);
  const [preference,setPreference]=useState(()=>({scope,on:savedSound(scope)}));
  const sound=preference.scope===scope?preference.on:savedSound(scope);
  const active=useRef(null);
  active.current={sound,scope,enabled};
  const [count,setCount]=useState(0);
  const [updatedCount,setUpdatedCount]=useState(0);
  const [error,setError]=useState('');
  const setSound=on=>{
    active.current={sound:on,scope,enabled};
    if(!on)pendingAudio.current=null;
    setPreference({scope,on});
    try{localStorage.setItem(soundKey(scope),on?'on':'off');}catch{}
  };
  const unlockAudio=useCallback(()=>{
    if(!active.current?.sound||!active.current.enabled)return;
    try{
      const AudioContext=window.AudioContext||window.webkitAudioContext;
      if(!AudioContext)throw new Error('Unavailable');
      if(!audio.current||audio.current.state==='closed')audio.current=new AudioContext();
      const context=audio.current;
      const pending=pendingAudio.current;
      context.resume().then(()=>{
        if(audio.current!==context||!active.current.sound||!active.current.enabled)return;
        setError('');
        if(!pending||pendingAudio.current!==pending||pending.scope!==active.current.scope)return;
        // A successful resume call can still leave mobile audio suspended.
        if(context.state&&context.state!=='running')return;
        pendingAudio.current=null;
        if(Date.now()-pending.at>60000)return;
        const oscillator=context.createOscillator();const gain=context.createGain();
        oscillator.connect(gain);gain.connect(context.destination);
        oscillator.frequency.value=880;
        gain.gain.setValueAtTime(0.2,context.currentTime);
        gain.gain.exponentialRampToValueAtTime(0.001,context.currentTime+0.6);
        oscillator.onended=()=>{oscillator.disconnect?.();gain.disconnect?.();};
        oscillator.start();oscillator.stop(context.currentTime+0.6);
      }).catch(()=>{/* Keep a recent alert queued for the next user gesture. */});
    }catch{setError('This browser could not enable sound. Visual alerts remain available.');}
  },[]);
  const queueSound=useCallback(()=>{
    if(!active.current?.sound||!active.current.enabled)return;
    pendingAudio.current={scope:active.current.scope,at:Date.now()};
    unlockAudio();
  },[unlockAudio]);
  useEffect(()=>{
    if(!enabled)return;
    if(!seen.current||seen.current.scope!==scope){seen.current={scope,orders:null};setCount(0);setUpdatedCount(0);}
    if(!orders)return;
    if(!seen.current.orders){seen.current.orders=new Map(orders.map(order=>[order.id,order.addition_id||null]));return;}
    const fresh=orders.filter(order=>!seen.current.orders.has(order.id)&&!terminalOrderStatus(order.status));
    const updated=orders.filter(order=>seen.current.orders.has(order.id)&&order.addition_id&&seen.current.orders.get(order.id)!==order.addition_id&&!terminalOrderStatus(order.status));
    orders.forEach(order=>seen.current.orders.set(order.id,order.addition_id||null));
    if(!fresh.length&&!updated.length)return;
    setCount(value=>value+fresh.length);setUpdatedCount(value=>value+updated.length);
    queueSound();
  },[orders,scope,enabled,queueSound]);
  useEffect(()=>{
    if(!enabled||!sound){pendingAudio.current=null;return;}
    if(pendingAudio.current?.scope!==scope)pendingAudio.current=null;
    unlockAudio();
    window.addEventListener('pointerdown',unlockAudio);
    window.addEventListener('keydown',unlockAudio);
    window.addEventListener('focus',unlockAudio);
    const visible=()=>{if(document.visibilityState==='visible')unlockAudio();};
    document.addEventListener('visibilitychange',visible);
    return()=>{window.removeEventListener('pointerdown',unlockAudio);window.removeEventListener('keydown',unlockAudio);window.removeEventListener('focus',unlockAudio);document.removeEventListener('visibilitychange',visible);};
  },[sound,enabled,scope,unlockAudio]);
  useEffect(()=>{if(!enabled){seen.current=null;setCount(0);setUpdatedCount(0);}},[enabled]);
  useEffect(()=>{
    if(!enabled||(!count&&!updatedCount))return;
    const previous=document.title;
    const label=updatedCount?`${count+updatedCount} order alerts`:`${count} new ${count===1?'order':'orders'}`;
    document.title=`(${label}) ${previous}`;
    return()=>{document.title=previous;};
  },[count,updatedCount,enabled]);
  useEffect(()=>()=>{const context=audio.current;audio.current=null;pendingAudio.current=null;context?.close().catch(()=>{});},[]);
  const toggleSound=()=>{
    setError('');
    if(sound){setSound(false);return;}
    setSound(true);unlockAudio();
  };
  const unread=count+updatedCount;
  return <Flex align="center" gap="3" wrap="wrap">
    <Button size="1" variant="soft" color="gray" onClick={toggleSound} aria-pressed={sound}>{sound?<Volume2 size={15}/>:<VolumeX size={15}/>}Sound {sound?'on':'off'}</Button>
    <span role="status" aria-live="polite">{count>0&&<Badge color="amber"><Bell size={13}/>{count} new {count===1?'order':'orders'}</Badge>}{updatedCount>0&&<Badge color="amber"><Bell size={13}/>{updatedCount} order {updatedCount===1?'update':'updates'}</Badge>}</span>
    {unread>0&&onOpenOrders&&<Button size="1" variant="soft" onClick={onOpenOrders}>View orders</Button>}
    {unread>0&&<Button size="1" variant="ghost" onClick={()=>{setCount(0);setUpdatedCount(0);pendingAudio.current=null;}}>Dismiss alert</Button>}
    {sound&&error&&<Text size="1" color="amber">{error}</Text>}
  </Flex>;
}
